import { supabase } from '../lib/supabase';

export type PublicProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  alias: string | null;
  bio: string | null;
  location: string | null;
  website: string | null;
  is_public: boolean;
  updated_at?: string | null;
  created_at?: string | null;
  profile_picture_url?: string | null;
  banner_image_url?: string | null;
};

export class PublicProfilesService {
  static async getPublicProfiles(params?: {
    search?: string;
    limit?: number;
  }): Promise<PublicProfile[]> {
    const search = String(params?.search || '').trim();
    const limit = params?.limit ?? 60;

    const base = supabase
      .from('users')
      .select('id, first_name, last_name, alias, bio, location, website, is_public, created_at, updated_at')
      .eq('is_public', true)
      .eq('is_admin', false)
      .order('updated_at', { ascending: false })
      .limit(limit);

    const q = search.length >= 2 ? search : '';
    const query = q
      ? base.or(
          [
            `alias.ilike.%${q}%`,
            `first_name.ilike.%${q}%`,
            `last_name.ilike.%${q}%`,
            `bio.ilike.%${q}%`,
            `location.ilike.%${q}%`,
          ].join(',')
        )
      : base;

    const { data: users, error } = await query;
    if (error) throw error;

    const rows = (users as any[] | null) ?? [];
    const ids = rows.map(r => r.id).filter(Boolean);

    let imagesByUser: Record<string, { profile_picture_url?: string | null; banner_image_url?: string | null }> = {};
    if (ids.length > 0) {
      const { data: imgs, error: imgErr } = await supabase
        .from('profile_images')
        .select('user_id, profile_picture_url, banner_image_url')
        .in('user_id', ids);

      if (!imgErr && imgs) {
        imagesByUser = (imgs as any[]).reduce((acc, cur) => {
          acc[String(cur.user_id)] = {
            profile_picture_url: cur.profile_picture_url ?? null,
            banner_image_url: cur.banner_image_url ?? null,
          };
          return acc;
        }, {} as Record<string, { profile_picture_url?: string | null; banner_image_url?: string | null }>);
      }
    }

    return rows.map((u) => {
      const img = imagesByUser[String(u.id)] || {};
      return {
        id: u.id,
        first_name: u.first_name ?? null,
        last_name: u.last_name ?? null,
        alias: u.alias ?? null,
        bio: u.bio ?? null,
        location: u.location ?? null,
        website: u.website ?? null,
        is_public: Boolean(u.is_public),
        created_at: u.created_at ?? null,
        updated_at: u.updated_at ?? null,
        profile_picture_url: img.profile_picture_url ?? null,
        banner_image_url: img.banner_image_url ?? null,
      } satisfies PublicProfile;
    });
  }
}
